import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { API, apiClient as axios } from "../../lib/api";
import { Toast } from "../../components/ui";
import { Button } from "../../components/ui";
import { RefreshCw, Plus, Loader2 } from "lucide-react";
import ProductsTable from "./components/ProductsTable";
import ProductModal from "./components/ProductModal";

export default function Products({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/products`, { headers: authHeaders });
      setProducts(res.data?.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleRowClick = useCallback((product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  }, []);

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveProduct = async (productData) => {
    try {
      setSubmitting(true);

      if (selectedProduct) {
        // Update existing product
        await axios.put(
          `${API}/products/${selectedProduct.id}`,
          productData,
          { headers: authHeaders }
        );
        toast.success("Product updated successfully!");
      } else {
        // Create new product
        await axios.post(
          `${API}/products`,
          productData,
          { headers: authHeaders }
        );
        toast.success("Product created successfully!");
      }

      setModalOpen(false);
      setSelectedProduct(null);
      await loadProducts();
    } catch (e) {
      const errorMessage = e?.response?.data?.error || "Failed to save product";
      toast.error(errorMessage);
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      setDeleting(true);
      await axios.delete(`${API}/products/${productId}`, { headers: authHeaders });
      toast.success("Product deleted successfully!");
      setModalOpen(false);
      setSelectedProduct(null);
      await loadProducts();
    } catch (e) {
      const errorMessage = e?.response?.data?.error || "Failed to delete product";
      toast.error(errorMessage);
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page">
      <Toast />
      <div className="toolbar">
        <div>
          <div className="title">Products Management</div>
          <div className="subtitle">Manage product catalog and pricing</div>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <Button
            variant="outline"
            onClick={loadProducts}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
          <Button
            onClick={handleAddProduct}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>
      <ProductsTable
        loading={loading}
        products={products}
        onRowClick={handleRowClick}
      />
      <ProductModal
        open={modalOpen}
        onClose={handleCloseModal}
        product={selectedProduct}
        onSave={handleSaveProduct}
        onDelete={handleDeleteProduct}
        submitting={submitting}
        deleting={deleting}
      />
    </div>
  );
}

